<script setup lang="ts">
import type {MenuItemInterface} from "~~/server/types/menu";
import type {SettingsInterface} from "~~/server/types/settings";
import {ref} from "vue";

const props = defineProps<{
    items: MenuItemInterface[];
    settings: SettingsInterface;
}>();
const mobileOpen = defineModel<boolean>("mobileOpen", { required: true });

/** Track which root node is open (single-open accordion) */
const openSet = ref<Set<number>>(new Set());
const isOpen = (i: number) => openSet.value.has(i);
const toggle = (i: number) => {
    const s = new Set(openSet.value);
    if (s.has(i)) s.delete(i);
    else {
        s.clear();
        s.add(i);
    }
    openSet.value = s;
};

const mobileActive = ref<string>(""); // за бъдеща употреба, ако искаш trail от индекси

const closeMobile = () => {
    mobileOpen.value = false;
    mobileActive.value = "";
    openSet.value.clear();
};

/** Keyboard: allow Space/Enter to toggle */
const onToggleKeydown = (e: KeyboardEvent, idx: number) => {
    if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggle(idx);
    }
};
</script>

<template>
    <el-drawer
            v-model="mobileOpen"
            :title="$t('navigation_menu')"
            size="100%"
            direction="ltr"
            :with-header="true"
            :lock-scroll="true"
            modal-class="overlay"
            style="--el-drawer-bg-color: var(--nav-mobile-bg);"
            header-class="m-0 "
    >
        <template #header>
            <div class="m-header">
                <NuxtLinkLocale
                        to="/"
                        class="m-logo"
                        @click="closeMobile"
                        aria-label="Go to homepage"
                >
                  <picture>
                    <source media="(min-width: 700px)" :srcset="settings?.logo" />
                    <source media="(max-width: 699px)" :srcset="settings?.logo_mobile || settings?.logo" />
                    <img
                      :src="settings?.logo"
                      :alt="settings?.site_title || 'Logo'"
                      loading="lazy"
                      decoding="async"
                      :width="settings?.logo_width"
                      :height="settings?.logo_height"
                    />
                  </picture>

                </NuxtLinkLocale>

                <!-- Optional right-side actions space -->
                <div class="m-actions" aria-hidden="true"></div>
            </div>
        </template>

        <!-- Mobile menu -->
        <nav class="mobile_menu m-drawer " role="navigation" aria-label="Mobile navigation">
            <ul class="m-list" role="tree">
                <li
                        v-for="(item, idx) in props.items"
                        :key="idx"
                        class="m-node"
                        :class="{ 'is-open': isOpen(idx)  }"
                        role="treeitem"
                        :aria-expanded="item.children?.length ? isOpen(idx) : undefined"
                >
                    <NuxtLinkLocale
                            v-if="item.design === 'link' || !item.children?.length"
                            :to="item.url"
                            class="m-row m-link"
                            exact-active-class="is-current"
                            @click="closeMobile"
                    >

                        <span class="m-title">{{ item.name }}</span>
                        <svg
                                class="m-chevron"
                                viewBox="0 0 24 24"
                                width="20"
                                height="20"
                                aria-hidden="true"
                        >
                            <path d="M9 6l6 6-6 6" fill="none" stroke="currentColor" stroke-width="2" />
                        </svg>
                    </NuxtLinkLocale>

                    <!-- Toggle + submenu -->
                    <template v-else>
                        <button
                                type="button"
                                class="m-row m-toggle"
                                :aria-expanded="isOpen(idx)"
                                :aria-controls="'m-sub-' + idx"
                                @click="toggle(idx)"
                                @keydown="onToggleKeydown($event, idx)"
                        >
                            <span class="m-title">{{ item.name }}</span>
                            <svg
                                    class="m-caret"
                                    viewBox="0 0 24 24"
                                    width="20"
                                    height="20"
                                    aria-hidden="true"
                            >
                                <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" stroke-width="2" />
                            </svg>
                        </button>

                        <transition name="scale-fade">
                            <ul
                                    v-show="isOpen(idx)"
                                    :id="'m-sub-' + idx"
                                    class="sub_menu"
                                    role="group"
                            >
                                <li
                                        v-for="(c, ck) in item.children"
                                        :key="ck"
                                        class="m-sub-item"
                                >
                                    <template v-if="c.children?.length">
                                        <NuxtLinkLocale
                                                :to="c.url"
                                                class="m-title fs--11 fw-semibold "
                                                @click="closeMobile"
                                        >
                                          <img class="icon-category" v-if="c.icon" :src="c.icon">
                                          {{ c.name }}
                                        </NuxtLinkLocale>
                                        <ul class="m-sub2 fw-normal fs--11" role="group">
                                            <li
                                                    v-for="(inner, ik) in c.children"
                                                    :key="ik"
                                            >
                                                <NuxtLinkLocale :to="inner.url" @click="closeMobile">
                                                    {{ inner.name }}
                                                </NuxtLinkLocale>
                                            </li>
                                        </ul>
                                    </template>
                                  <template v-else>
                                    <NuxtLinkLocale
                                        :to="c.url"
                                        :class="c.icon ? 'm-title fs--11 fw-semibold' : ''"
                                        @click="closeMobile"
                                    >
                                      <img class="icon-category" v-if="c.icon" :src="c.icon">
                                      {{ c.name }}
                                    </NuxtLinkLocale>
                                  </template>
                                </li>
                                <li class="m-sub-all">
                                    <NuxtLinkLocale
                                            :to="item.url"
                                            class="m-sub-all-link el-button el-button--primary el-button--default is-round"
                                            @click="closeMobile"
                                    >
                                        {{$t('view_all')}}
                                    </NuxtLinkLocale>
                                </li>
                            </ul>
                        </Transition>
                    </template>
                </li>
            </ul>
        </nav>
    </el-drawer>
</template>

<style scoped lang="scss">

:host,
.m-drawer {
  --divider: var(--nav-mobile-color);
  --ring: var(--nav-mobile-color);
  --pad-x: 18px;
}

.m-drawer :deep(.el-drawer__body) {
  padding: 0;
}
.overlay {
  backdrop-filter: saturate(120%) blur(6px);
}

/* Sticky header with glass effect */
.m-header {
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 14px var(--pad-x);
  background: var(--nav-mobile-bg);
  backdrop-filter: blur(8px);
  border-bottom: 1px solid var(--divider);
}

.m-logo {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  outline: none;
  border-radius: 10px;
  padding: 6px 8px;

  &:focus-visible {

  }
  img {
    width: 100%;
    height: auto;
    max-width: 80%;
  }
}

/* Base container */
.mobile_menu {
  color: var(--nav-mobile-color);
  -webkit-tap-highlight-color: transparent;

  ul,
  li {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  /* ===== List structure ===== */
  .m-list {
    display: block;
  }

  .m-node {
    border-bottom: 1px solid var(--divider);
    position: relative;
    .m-caret {
      transition: transform 0.22s ease, opacity .18s ease;
      opacity: .9;
    }
    &.is-open .m-caret {
      transform: rotate(180deg) translateY(.02em);
    }
  }

  /* ===== Row (both link and toggle) ===== */
  .m-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 52px;
    padding: 12px var(--pad-x);
    width: 100%;

    color: inherit;
    text-decoration: none;
    letter-spacing: .2px;

    border: 0;
    background: none;
    cursor: pointer;


    &:focus-visible {
      outline: none;

      border-radius: 10px;
    }
  }

  .m-link .m-chevron {
    opacity: 0;
    transition: transform .16s ease, opacity .16s ease;
  }


  .m-title {
    display: inline-block;
    margin-right: 10px;
    text-transform: uppercase;
    font-size: var(--fs--11);
    font-weight: var(--fw-semibold);
    text-align: left;
  }

  /* ===== Submenu (accordion panel) ===== */
  .sub_menu {

    /* "View all" link */
    > .m-sub-all > .m-sub-all-link {
      color: white;
      text-decoration: none;
      font-weight: var(--fw-semibold);
      margin: 15px 0 20px 15px;


      &:focus-visible {
        outline: none;

        border-radius: 10px;
      }
    }

    /* Level-1 children */
    > .m-sub-item {

      font-size: var(--fs--1);

      .m-title {
        display: flex;
        padding: 12px var(--pad-x) 6px;
        font-weight: var(--fw-semibold);
        text-decoration: none;
        align-items: center;
        gap:16px;
        text-transform: uppercase;

        &:hover {
          color: var(--nav-bg);
        }
      }
      .icon-category{
        border-radius: 10px;
        width: 50px;
        height: 50px;
      }

      > a {
        display: block;
        padding: 8px var(--pad-x);
        text-decoration: none;
        font-weight: var(--fw-semibold);

        &:hover {
          color: var(--nav-panel-text-hover);
        }
        &:focus-visible {
          outline: none;

          border-radius: 10px;
        }
      }
    }

    /* Level-2 list */
    .m-sub2 {
      padding: 0 0 10px;


      li > a {
        display: block;
        padding: 10px calc(var(--pad-x) + 10px);
        padding-left: 0;
        font-weight: var(--fw-semibold);
        text-decoration: none;
        border-radius: 10px;
        margin: 2px var(--pad-x);

        &:hover {
          background: color-mix(in oklab, var(--nav-bg) 8%, transparent);
        }
        &:focus-visible {
          outline: none;

        }
      }

      li.is-active > a {
        background: color-mix(in oklab, var(--nav-bg) 12%, transparent);
      }
    }
  }

  /* ===== Reduced motion ===== */
  @media (prefers-reduced-motion: reduce) {
    * {
      transition: none !important;
      animation: none !important;
    }
  }
}

.scale-fade-enter-from,
.scale-fade-leave-to { opacity: 0; transform: translateY(-6px) scale(.98); }
.scale-fade-enter-active,
.scale-fade-leave-active { transition: transform .16s ease, opacity .16s ease; }
.scale-fade-enter-to,
.scale-fade-leave-from { opacity: 1; transform: translateY(0) scale(1); }

</style>
